import { useEffect, useMemo } from "react";
import { useRecoilState, useRecoilValue } from "recoil";
import Select from "src/common/components/select";
import { IDropdownOption } from "src/constants/interfaces";
import { useTranslation } from "src/hooks/translation";
import { getSystemOptionVariantsCount } from "src/rules/calculation/systemHelpers";
import { getEffectiveBankerCount, getEffectiveSelectionsCount } from "src/common/helpers/betBuilder";
import mainStore from "src/stores/main";
import selectionsStore from "src/stores/selections";
import EachWayToggle from "../eachWayToggle";
import { getSystemSelectOptions } from "./helpers";
import { openDeferPopup } from "src/common/helpers/view";
import { CALCULATOR_POPUP } from "src/components/popups/constants";
import { useConfig } from "src/hooks/config";

interface IProps {
	isEachWayAvailable: boolean;
}

const SystemOptions = ({ isEachWayAvailable }: IProps) => {
	const t = useTranslation();
	const { selections } = useRecoilValue(selectionsStore);
	const [mainState, setMainState] = useRecoilState(mainStore);
	const config = useConfig();

	// Bet builder cards collapse to ONE leg each for system math —
	// 2 BB cards with 2 legs apiece must count as 2, not 4.
	const [effectiveCount, bankersCount, containsRacingEvent] = useMemo(() => [
		getEffectiveSelectionsCount(selections),
		getEffectiveBankerCount(selections),
		selections.some(item => item.event.isRacingEvent),
	], [selections]);

	const changeCombinationCount = ({ value }: IDropdownOption<string, number>) => {
		setMainState({ ...mainState, systemCombinationCount: value });
	};

	const selectOptions = getSystemSelectOptions(effectiveCount, bankersCount);

	useEffect(() => {
		// Both branches must compare the target value BEFORE calling
		// setMainState — `mainState` is in the deps, and setMainState
		// always produces a new object reference, so an unconditional
		// call here loops forever and freezes the betslip.
		if (selectOptions.length && effectiveCount >= 3) {
			const needResetCombinationsCount = !selectOptions.some(item => item.value === mainState.systemCombinationCount);
			if (needResetCombinationsCount && mainState.systemCombinationCount !== selectOptions[0].value) {
				setMainState({ ...mainState, systemCombinationCount: selectOptions[0].value });
			}
		} else if (effectiveCount >= 3 && mainState.systemCombinationCount !== 2) {
			setMainState({ ...mainState, systemCombinationCount: 2 })
		}
	}, [selections, selectOptions, bankersCount, effectiveCount, mainState, setMainState]);

	if (effectiveCount < 3) return null;

	const optionVariantsCount = getSystemOptionVariantsCount(
		mainState.systemCombinationCount - bankersCount,
		effectiveCount - bankersCount,
	);

	return (
		<div className="B-betslip-system-options">
			<h3 className="B-bet-quantity-txt">
				{t("Quantity of Variants")}: {optionVariantsCount}
			</h3>

			<Select
				value={mainState.systemCombinationCount}
				options={selectOptions}
				onChange={changeCombinationCount}
			/>

			{config.enableSystemCalculator && (
				<button
					disabled={containsRacingEvent}
					className="B-icon-calc-block global-icon icon-calculator"
					onClick={() => openDeferPopup(CALCULATOR_POPUP)}
				/>
			)}

			{isEachWayAvailable && <EachWayToggle />}
		</div>
	);
};

export default SystemOptions;
